// base exports from dayjs
import dayjs from 'dayjs';
// plugins
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import duration from 'dayjs/plugin/duration.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';

// localized-format plugin + bundled locales + default locale (side-effect setup)
import './locale/dayjsLocaleSetup';

// custom utils
import { formatDate, formatDateTime, formatTime } from './formatDate/formatDate';
import { formatDateRange } from './formatDateRange/formatDateRange';
import { formatDateToDateTimeIsoString } from './formatDateToDateTimeIsoString/formatDateToDateTimeIsoString';
import { formatDateToIsoString } from './formatDateToIsoString';
import { formatElapsedMilliseconds, formatElapsedTime } from './formatElapsedTime/formatElapsedTime';
import { formatTimeToIsoString } from './formatTimeToIsoString';
import { getMillisecondsUntil } from './getMillisecondsUntil';
import { getMinutesAsMilliseconds } from './getMinutesAsMilliseconds';
import { getTimeRemaining } from './getTimeRemaining/getTimeRemaining';
import { getTimezoneName } from './getTimezoneName';
import { getTimezoneOffset } from './getTimezoneOffset';
import { isWeekend } from './isWeekend/isWeekend';
import { useElapsedMilliseconds } from './useElapsedMilliseconds/useElapsedMilliseconds';
import { useElapsedTime } from './useElapsedTime/useElapsedTime';
import { useTimeRemaining } from './useTimeRemaining/useTimeRemaining';

export type { Dayjs } from 'dayjs';
export type { TimeRemaining } from './getTimeRemaining/getTimeRemaining';

export const isDayjs = (d: unknown): d is ReturnType<typeof dayjs> => {
  return dayjs.isDayjs(d);
};

export {
  dayjs,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatDateToDateTimeIsoString,
  formatDateToIsoString,
  formatElapsedMilliseconds,
  formatElapsedTime,
  formatTime,
  formatTimeToIsoString,
  getMillisecondsUntil,
  getMinutesAsMilliseconds,
  getTimeRemaining,
  getTimezoneName,
  getTimezoneOffset,
  isWeekend,
  useElapsedMilliseconds,
  useElapsedTime,
  useTimeRemaining,
};

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

// Date-formatting locale: the format* helpers use dayjs's global locale (localized format
// tokens). The app sets it from the browser at startup via setDateLocale(detectBrowserLocale()).
export { DEFAULT_LOCALE } from './locale/constants';
export { setDateLocale } from './locale/dateLocale';
export { duration, isSameOrAfter, relativeTime };
